/**
 * Mic -> (optional RNNoise WASM worklet) -> MediaStream for WebRTC.
 * Browser noiseSuppression stays off to avoid double-filtering.
 */

const PUBLIC_BASE = `${process.env.PUBLIC_URL || ''}/vendor/rnnoise`;
const WORKLET_URL = `${PUBLIC_BASE}/rnnoiseWorklet.js`;
const WASM_URL = `${PUBLIC_BASE}/rnnoise.wasm`;
const WASM_SIMD_URL = `${PUBLIC_BASE}/rnnoise_simd.wasm`;

let wasmBinaryPromise = null;
const workletReady = new WeakSet();

const loadSuppressorModule = () => {
  // CJS entry works with CRA / webpack 4; ESM package.exports often does not.
  // eslint-disable-next-line global-require, import/no-extraneous-dependencies
  return require('@sapphi-red/web-noise-suppressor');
};

const getWasmBinary = async () => {
  if (!wasmBinaryPromise) {
    const { loadRnnoise } = loadSuppressorModule();
    wasmBinaryPromise = loadRnnoise({
      url: WASM_URL,
      simdUrl: WASM_SIMD_URL,
    }).catch((error) => {
      wasmBinaryPromise = null;
      throw error;
    });
  }
  return wasmBinaryPromise;
};

const ensureWorklet = async (audioContext) => {
  if (workletReady.has(audioContext)) {
    return;
  }
  await audioContext.audioWorklet.addModule(WORKLET_URL);
  workletReady.add(audioContext);
};

const captureRawMic = async () => navigator.mediaDevices.getUserMedia({
  audio: {
    echoCancellation: true,
    autoGainControl: true,
    noiseSuppression: false,
    channelCount: 1,
  },
  video: false,
});

/**
 * @returns {Promise<{
 *   stream: MediaStream,
 *   setNoiseSuppression: (enabled: boolean) => Promise<boolean>,
 *   destroy: () => void,
 * }>}
 */
export const createRnnoiseMicPipeline = async (noiseSuppression = true) => {
  const rawStream = await captureRawMic();
  let audioContext = null;
  let source = null;
  let micGain = null;
  let destination = null;
  let rnnoiseNode = null;
  let screenSource = null;
  let enabled = false;
  let destroyed = false;
  let resumeCtx = null;

  const safeDisconnect = (node) => {
    try {
      node?.disconnect?.();
    } catch (_) {
      // already disconnected
    }
  };

  const connectBypass = () => {
    safeDisconnect(source);
    safeDisconnect(micGain);
    safeDisconnect(rnnoiseNode);
    source.connect(micGain);
    micGain.connect(destination);
  };

  const connectRnnoise = async () => {
    if (!rnnoiseNode) {
      await ensureWorklet(audioContext);
      const wasmBinary = await getWasmBinary();
      const { RnnoiseWorkletNode } = loadSuppressorModule();
      rnnoiseNode = new RnnoiseWorkletNode(audioContext, {
        maxChannels: 1,
        wasmBinary,
      });
    }
    safeDisconnect(source);
    safeDisconnect(micGain);
    safeDisconnect(rnnoiseNode);
    source.connect(micGain);
    micGain.connect(rnnoiseNode);
    rnnoiseNode.connect(destination);
  };

  const setMicMuted = (muted) => {
    if (micGain) {
      micGain.gain.value = muted ? 0 : 1;
    }
    rawStream.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  };

  const setScreenAudioStream = (stream) => {
    if (destroyed || !audioContext || !destination) {
      return false;
    }
    safeDisconnect(screenSource);
    screenSource = null;
    const tracks = stream?.getAudioTracks?.() || [];
    if (!tracks.length) {
      return false;
    }
    try {
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
      screenSource = audioContext.createMediaStreamSource(new MediaStream(tracks));
      screenSource.connect(destination);
      return true;
    } catch (error) {
      console.warn('[call] screen audio mix failed', error);
      screenSource = null;
      return false;
    }
  };

  try {
    audioContext = new AudioContext({ sampleRate: 48000 });
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    source = audioContext.createMediaStreamSource(rawStream);
    destination = audioContext.createMediaStreamDestination();
    try {
      destination.channelCount = 2;
      destination.channelCountMode = 'explicit';
    } catch (_) {
      // ignore unsupported channel config
    }
    micGain = audioContext.createGain();
    micGain.gain.value = 1;
    connectBypass();
    resumeCtx = () => {
      if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
    };
    audioContext.addEventListener('statechange', resumeCtx);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', resumeCtx);
    }

    if (noiseSuppression) {
      await connectRnnoise();
      enabled = true;
    }
  } catch (error) {
    // Fallback: raw mic only (e.g. worklet/wasm unavailable).
    console.warn('[call] RNNoise pipeline unavailable, using raw mic', error);
    destroyed = true;
    return {
      stream: rawStream,
      setNoiseSuppression: async () => false,
      setMicMuted,
      setScreenAudioStream: () => false,
      destroy: () => {
        rawStream.getTracks().forEach((track) => track.stop());
      },
    };
  }

  return {
    get stream() {
      return destination.stream;
    },
    async setNoiseSuppression(nextEnabled) {
      if (destroyed) {
        return false;
      }
      const next = Boolean(nextEnabled);
      if (next === enabled) {
        return true;
      }
      try {
        if (next) {
          await connectRnnoise();
        } else {
          connectBypass();
        }
        enabled = next;
        return true;
      } catch (error) {
        console.warn('[call] failed to toggle RNNoise', error);
        return false;
      }
    },
    setMicMuted,
    setScreenAudioStream,
    destroy: () => {
      if (destroyed) {
        return;
      }
      destroyed = true;
      if (resumeCtx) {
        try {
          audioContext?.removeEventListener('statechange', resumeCtx);
        } catch (_) {
          // ignore
        }
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', resumeCtx);
        }
      }
      try {
        rnnoiseNode?.destroy?.();
      } catch (_) {
        // ignore
      }
      safeDisconnect(source);
      safeDisconnect(micGain);
      safeDisconnect(screenSource);
      safeDisconnect(rnnoiseNode);
      try {
        audioContext?.close();
      } catch (_) {
        // ignore
      }
      rawStream.getTracks().forEach((track) => track.stop());
      // destination tracks are derived; stopping raw is enough
      destination?.stream?.getTracks?.()?.forEach((track) => {
        try {
          track.stop();
        } catch (_) {
          // ignore
        }
      });
    },
  };
};
